import React, { useState, useEffect, useMemo } from 'react';
import { quarantineAPI, warehouseAPI, vansAPI, itemsAPI } from '../lib/api';
import { useLiveRefresh } from '../hooks/useLiveRefresh';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { IntegerQuantityInput } from '../components/IntegerQuantityInput';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { 
  Plus, Loader2, AlertTriangle, Filter, Search, 
  CheckCircle2, Package, Warehouse, Truck, Calendar, ClipboardList, X
} from 'lucide-react';
import { MobileFAB } from '../components/MobileFAB';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';
import { ItemSearchCombobox } from '../components/ItemSearchCombobox';

// Status badge colors
const STATUS_COLORS = {
  'active': 'bg-yellow-100 text-yellow-800 border-yellow-300',
  'resolved': 'bg-green-100 text-green-800 border-green-300',
};

// Reason display names and colors
const REASON_CONFIG = {
  'defective': { label: 'Defective', color: 'bg-red-100 text-red-800' },
  'warranty_claim': { label: 'Warranty Claim', color: 'bg-orange-100 text-orange-800' },
  'pending_inspection': { label: 'Pending Inspection', color: 'bg-blue-100 text-blue-800' },
  'recalled': { label: 'Recalled', color: 'bg-purple-100 text-purple-800' },
};

// Resolution action display names
const RESOLUTION_ACTIONS = {
  'returned_to_stock': 'Return to Stock',
  'scrapped': 'Scrapped',
  'returned_to_supplier': 'Returned to Supplier',
  'warranty_replaced': 'Warranty Replaced',
};

// Format timestamp to local time
const formatLocalTime = (utcTimestamp, formatStr) => {
  if (!utcTimestamp) return '';
  try {
    let date;
    if (utcTimestamp.endsWith('Z')) {
      date = parseISO(utcTimestamp);
    } else if (utcTimestamp.includes('+') || utcTimestamp.includes('-', 10)) {
      date = parseISO(utcTimestamp);
    } else {
      date = parseISO(utcTimestamp + 'Z');
    }
    return format(date, formatStr);
  } catch (error) {
    return format(new Date(utcTimestamp), formatStr);
  }
};

export const Quarantine = () => {
  const { isOwner, isOffice, isTech } = useAuth();
  const canResolve = isOwner || isOffice;
  
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState({
    total_active: 0,
    total_resolved: 0,
    by_reason: {},
    by_location_type: {},
    total_quarantined_items: 0,
  });
  const [warehouses, setWarehouses] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  
  // Filter state
  const [statusFilter, setStatusFilter] = useState('active');
  const [reasonFilter, setReasonFilter] = useState('all');
  const [locationTypeFilter, setLocationTypeFilter] = useState('all');
  const [searchFilter, setSearchFilter] = useState('');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  
  // Create modal state
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    item_id: null,
    item_name: '',
    location_type: 'warehouse',
    warehouse_id: '',
    van_id: '',
    quantity: 1,
    reason: 'defective',
    notes: '',
    hold_until_date: '',
  });
  const [saving, setSaving] = useState(false);
  
  // View/Resolve modal state
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);
  const [resolveMode, setResolveMode] = useState(false);
  const [resolveData, setResolveData] = useState({
    resolution_action: 'returned_to_stock',
    resolution_notes: '',
  });
  const [resolving, setResolving] = useState(false);

  // Fetch data
  const fetchData = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (reasonFilter !== 'all') params.reason = reasonFilter;
      if (locationTypeFilter !== 'all') params.location_type = locationTypeFilter;
      
      const [recordsRes, summaryRes, warehousesRes, vehiclesRes] = await Promise.all([
        quarantineAPI.list(params),
        quarantineAPI.getSummary(),
        warehouseAPI.listWarehouses(),
        vansAPI.list(),
      ]);
      
      setRecords(recordsRes.data);
      setSummary(summaryRes.data);
      setWarehouses(warehousesRes.data || []);
      setVehicles((vehiclesRes.data || []).filter(v => v.is_active));
    } catch (error) {
      console.error('Error fetching quarantine data:', error);
      if (!silent) toast.error('Failed to load quarantine records');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [statusFilter, reasonFilter, locationTypeFilter]);

  // Phase 6: silent revalidation; open resolve/move dialogs keep their state
  useLiveRefresh(() => fetchData({ silent: true }));

  // Filtered records based on search
  const filteredRecords = useMemo(() => {
    if (!searchFilter.trim()) return records;
    const search = searchFilter.toLowerCase();
    return records.filter(r => 
      r.item_name?.toLowerCase().includes(search) ||
      r.item_sku?.toLowerCase().includes(search) ||
      r.warehouse_name?.toLowerCase().includes(search) ||
      r.van_name?.toLowerCase().includes(search) ||
      r.quarantined_by_name?.toLowerCase().includes(search)
    );
  }, [records, searchFilter]);

  // Reset create form
  const resetCreateForm = () => {
    setFormData({
      item_id: null,
      item_name: '',
      location_type: 'warehouse',
      warehouse_id: warehouses[0]?.id || '',
      van_id: vehicles[0]?.id || '',
      quantity: 1,
      reason: 'defective',
      notes: '',
      hold_until_date: '',
    });
  };

  // Handle create submit
  const handleCreate = async () => {
    // Validation - item_id is required (must select from dropdown)
    if (!formData.item_id) {
      toast.error('Please select an item from the dropdown');
      return;
    }
    if (formData.location_type === 'warehouse' && !formData.warehouse_id) {
      toast.error('Please select a warehouse');
      return;
    }
    if (formData.location_type === 'vehicle' && !formData.van_id) {
      toast.error('Please select a vehicle');
      return;
    }
    if (formData.quantity < 1) {
      toast.error('Quantity must be at least 1');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        item_id: formData.item_id,
        location_type: formData.location_type,
        quantity: parseInt(formData.quantity),
        reason: formData.reason,
        notes: formData.notes || null,
        hold_until_date: formData.hold_until_date ? new Date(formData.hold_until_date).toISOString() : null,
      };
      
      if (formData.location_type === 'warehouse') {
        payload.warehouse_id = formData.warehouse_id;
      } else {
        payload.van_id = formData.van_id;
      }
      
      await quarantineAPI.create(payload);
      toast.success('Quarantine record created');
      setCreateModalOpen(false);
      resetCreateForm();
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const status = error.response?.status;
      let message;
      if (typeof detail === 'string') {
        message = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        message = detail.map(d => d.msg || d.message || JSON.stringify(d)).join('; ');
      } else if (detail && typeof detail.message === 'string') {
        message = detail.message;
      } else if (status === 503 || status === 504) {
        message = 'Server is temporarily busy — please try again in a few seconds';
      } else if (error.response) {
        message = `Server error (${status})`;
      } else if (error.code === 'ECONNABORTED' || error.request) {
        message = 'Request timed out — the server may be under heavy load. Please retry.';
      } else {
        message = 'Failed to create quarantine record';
      }
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  // Handle resolve submit
  const handleResolve = async () => {
    if (!resolveData.resolution_notes.trim()) {
      toast.error('Resolution notes are required');
      return;
    }
    
    setResolving(true);
    try {
      await quarantineAPI.resolve(viewingRecord.id, {
        resolution_action: resolveData.resolution_action,
        resolution_notes: resolveData.resolution_notes,
      });
      toast.success('Quarantine record resolved');
      setViewModalOpen(false);
      setResolveMode(false);
      setResolveData({ resolution_action: 'returned_to_stock', resolution_notes: '' });
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      let message;
      if (typeof detail === 'string') {
        message = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        message = detail.map(d => d.msg || d.message || JSON.stringify(d)).join('; ');
      } else if (error.response) {
        message = `Server error (${error.response.status})`;
      } else if (error.request) {
        message = 'No response from server — check your connection';
      } else {
        message = 'Failed to resolve quarantine';
      }
      toast.error(message);
    } finally {
      setResolving(false);
    }
  };

  // Open view modal
  const openViewModal = (record) => {
    setViewingRecord(record);
    setResolveMode(false);
    setResolveData({ resolution_action: 'returned_to_stock', resolution_notes: '' });
    setViewModalOpen(true);
  };

  // Handle item selection in create form
  const handleItemSelect = ({ item_id, item_name }) => {
    setFormData(prev => ({
      ...prev,
      item_id,
      item_name,
    }));
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold font-heading text-foreground flex items-center gap-2">
              <AlertTriangle className="w-7 h-7 text-yellow-600" />
              Quarantine Hold
            </h1>
            <p className="text-muted-foreground mt-1 text-sm lg:text-base">
              Track defective, warranty, and recalled inventory
            </p>
          </div>
          <Button 
            onClick={() => {
              resetCreateForm();
              setCreateModalOpen(true);
            }}
            data-testid="create-quarantine-btn"
            className="hidden md:inline-flex"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Quarantine
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-yellow-600">{summary.total_active}</div>
              <div className="text-sm text-muted-foreground">Active Holds</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-green-600">{summary.total_resolved}</div>
              <div className="text-sm text-muted-foreground">Resolved</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-blue-600">{summary.total_quarantined_items}</div>
              <div className="text-sm text-muted-foreground">Total Qty Held</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold">
                {(summary.by_location_type?.warehouse || 0) + (summary.by_location_type?.vehicle || 0)}
              </div>
              <div className="text-sm text-muted-foreground flex items-center gap-2">
                <Warehouse className="w-3 h-3" /> {summary.by_location_type?.warehouse || 0}
                <Truck className="w-3 h-3 ml-2" /> {summary.by_location_type?.vehicle || 0}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              {/* Search */}
              <div className="relative flex-1 min-w-0">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by item, SKU, location..."
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  className="pl-9"
                  data-testid="quarantine-search"
                />
              </div>
              {/* Filters - stack on mobile */}
              <div className="flex gap-2 flex-wrap">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[130px]" data-testid="status-filter">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={reasonFilter} onValueChange={setReasonFilter}>
                  <SelectTrigger className="w-[150px]" data-testid="reason-filter">
                    <SelectValue placeholder="Reason" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Reasons</SelectItem>
                    <SelectItem value="defective">Defective</SelectItem>
                    <SelectItem value="warranty_claim">Warranty Claim</SelectItem>
                    <SelectItem value="pending_inspection">Pending Inspection</SelectItem>
                    <SelectItem value="recalled">Recalled</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={locationTypeFilter} onValueChange={setLocationTypeFilter}>
                  <SelectTrigger className="w-[140px]" data-testid="location-type-filter">
                    <SelectValue placeholder="Location" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Locations</SelectItem>
                    <SelectItem value="warehouse">Warehouse</SelectItem>
                    <SelectItem value="vehicle">Vehicle</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Records List */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : filteredRecords.length === 0 ? (
              <div className="text-center py-12">
                <AlertTriangle className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                <p className="text-muted-foreground">No quarantine records found</p>
              </div>
            ) : (
              <div className="divide-y">
                {filteredRecords.map((record) => (
                  <div
                    key={record.id}
                    className="p-4 hover:bg-muted/50 cursor-pointer transition-colors"
                    onClick={() => openViewModal(record)}
                    data-testid={`quarantine-record-${record.id}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-foreground truncate">
                            {record.item_name || 'Unknown Item'}
                          </span>
                          {record.item_sku && (
                            <Badge variant="outline" className="text-xs">
                              {record.item_sku}
                            </Badge>
                          )}
                          <Badge className={`${REASON_CONFIG[record.reason]?.color || 'bg-gray-100'} text-xs`}>
                            {REASON_CONFIG[record.reason]?.label || record.reason}
                          </Badge>
                          <Badge variant="outline" className={`text-xs ${STATUS_COLORS[record.status]}`}>
                            {record.status === 'active' ? 'Active' : 'Resolved'}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            {record.location_type === 'warehouse' ? (
                              <><Warehouse className="w-3.5 h-3.5" /> {record.warehouse_name}</>
                            ) : (
                              <><Truck className="w-3.5 h-3.5" /> {record.van_name}</>
                            )}
                          </span>
                          <span className="flex items-center gap-1">
                            <Package className="w-3.5 h-3.5" /> Qty: {record.quantity}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" /> 
                            {formatLocalTime(record.quarantined_at, 'MMM d, yyyy')}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {record.status === 'active' && canResolve && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              setViewingRecord(record);
                              setResolveMode(true);
                              setViewModalOpen(true);
                            }}
                            data-testid={`resolve-btn-${record.id}`}
                          >
                            <CheckCircle2 className="w-4 h-4 mr-1" />
                            Resolve
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create Quarantine Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
              New Quarantine Hold
            </DialogTitle>
            <DialogDescription>
              Place inventory on hold for defects, warranty claims, or inspection.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Item Search */}
            <div className="space-y-2">
              <Label>Item *</Label>
              <ItemSearchCombobox
                value={formData.item_name}
                itemId={formData.item_id}
                onChange={(val) => setFormData(prev => ({ ...prev, item_name: val }))}
                onSelect={handleItemSelect}
                placeholder="Search for an item..."
                data-testid="quarantine-item-search"
              />
            </div>
            
            {/* Location Type */}
            <div className="space-y-2">
              <Label>Location Type *</Label>
              <Select 
                value={formData.location_type} 
                onValueChange={(val) => setFormData(prev => ({ ...prev, location_type: val }))}
              >
                <SelectTrigger data-testid="location-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="warehouse">
                    <span className="flex items-center gap-2">
                      <Warehouse className="w-4 h-4" /> Warehouse
                    </span>
                  </SelectItem>
                  <SelectItem value="vehicle">
                    <span className="flex items-center gap-2">
                      <Truck className="w-4 h-4" /> Vehicle
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Location Select */}
            {formData.location_type === 'warehouse' ? (
              <div className="space-y-2">
                <Label>Warehouse *</Label>
                <Select 
                  value={formData.warehouse_id} 
                  onValueChange={(val) => setFormData(prev => ({ ...prev, warehouse_id: val }))}
                >
                  <SelectTrigger data-testid="warehouse-select">
                    <SelectValue placeholder="Select warehouse" />
                  </SelectTrigger>
                  <SelectContent>
                    {warehouses.map(wh => (
                      <SelectItem key={wh.id} value={wh.id}>{wh.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Vehicle *</Label>
                <Select 
                  value={formData.van_id} 
                  onValueChange={(val) => setFormData(prev => ({ ...prev, van_id: val }))}
                >
                  <SelectTrigger data-testid="vehicle-select">
                    <SelectValue placeholder="Select vehicle" />
                  </SelectTrigger>
                  <SelectContent>
                    {vehicles.map(v => (
                      <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Quantity */}
            <div className="space-y-2">
              <Label>Quantity *</Label>
              <IntegerQuantityInput
                min={1}
                value={formData.quantity}
                onChange={(val) => setFormData(prev => ({ ...prev, quantity: parseInt(val) || 1 }))}
                data-testid="quantity-input"
              />
            </div>
            
            {/* Reason */}
            <div className="space-y-2">
              <Label>Reason *</Label>
              <Select 
                value={formData.reason} 
                onValueChange={(val) => setFormData(prev => ({ ...prev, reason: val }))}
              >
                <SelectTrigger data-testid="reason-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="defective">Defective</SelectItem>
                  <SelectItem value="warranty_claim">Warranty Claim</SelectItem>
                  <SelectItem value="pending_inspection">Pending Inspection</SelectItem>
                  <SelectItem value="recalled">Recalled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Hold Until Date (Optional) */}
            <div className="space-y-2">
              <Label>Hold Until Date (Optional)</Label>
              <Input
                type="date"
                value={formData.hold_until_date}
                onChange={(e) => setFormData(prev => ({ ...prev, hold_until_date: e.target.value }))}
                data-testid="hold-until-input"
              />
            </div>
            
            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Additional details about this quarantine..."
                rows={3}
                data-testid="notes-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={saving} data-testid="create-submit-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create Quarantine
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View/Resolve Modal */}
      <Dialog open={viewModalOpen} onOpenChange={setViewModalOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {resolveMode ? (
                <>
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  Resolve Quarantine
                </>
              ) : (
                <>
                  <ClipboardList className="w-5 h-5" />
                  Quarantine Details
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {viewingRecord && (
            <div className="space-y-4 py-4">
              {/* Item Info */}
              <div className="bg-muted/50 rounded-lg p-4">
                <div className="font-semibold text-lg">{viewingRecord.item_name}</div>
                {viewingRecord.item_sku && (
                  <div className="text-sm text-muted-foreground">SKU: {viewingRecord.item_sku}</div>
                )}
                <div className="flex items-center gap-3 mt-2">
                  <Badge className={`${REASON_CONFIG[viewingRecord.reason]?.color || 'bg-gray-100'}`}>
                    {REASON_CONFIG[viewingRecord.reason]?.label || viewingRecord.reason}
                  </Badge>
                  <Badge variant="outline" className={STATUS_COLORS[viewingRecord.status]}>
                    {viewingRecord.status === 'active' ? 'Active' : 'Resolved'}
                  </Badge>
                </div>
              </div>
              
              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-muted-foreground">Location</div>
                  <div className="font-medium flex items-center gap-1">
                    {viewingRecord.location_type === 'warehouse' ? (
                      <><Warehouse className="w-4 h-4" /> {viewingRecord.warehouse_name}</>
                    ) : (
                      <><Truck className="w-4 h-4" /> {viewingRecord.van_name}</>
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground">Quantity</div>
                  <div className="font-medium">{viewingRecord.quantity}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Quarantined By</div>
                  <div className="font-medium">{viewingRecord.quarantined_by_name}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Quarantined At</div>
                  <div className="font-medium">
                    {formatLocalTime(viewingRecord.quarantined_at, 'MMM d, yyyy h:mm a')}
                  </div>
                </div>
                {viewingRecord.hold_until_date && (
                  <div className="col-span-2">
                    <div className="text-muted-foreground">Hold Until</div>
                    <div className="font-medium">
                      {formatLocalTime(viewingRecord.hold_until_date, 'MMM d, yyyy')}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Notes */}
              {viewingRecord.notes && (
                <div>
                  <div className="text-sm text-muted-foreground mb-1">Notes</div>
                  <div className="bg-muted/30 rounded p-3 text-sm">{viewingRecord.notes}</div>
                </div>
              )}
              
              {/* Resolution Info (if resolved) */}
              {viewingRecord.status === 'resolved' && (
                <div className="border-t pt-4 space-y-3">
                  <div className="font-semibold text-green-700 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" /> Resolution
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <div className="text-muted-foreground">Action</div>
                      <div className="font-medium">
                        {RESOLUTION_ACTIONS[viewingRecord.resolution_action] || viewingRecord.resolution_action}
                      </div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Resolved By</div>
                      <div className="font-medium">{viewingRecord.resolved_by_name}</div>
                    </div>
                    <div className="col-span-2">
                      <div className="text-muted-foreground">Resolved At</div>
                      <div className="font-medium">
                        {formatLocalTime(viewingRecord.resolved_at, 'MMM d, yyyy h:mm a')}
                      </div>
                    </div>
                  </div>
                  {viewingRecord.resolution_notes && (
                    <div>
                      <div className="text-sm text-muted-foreground mb-1">Resolution Notes</div>
                      <div className="bg-green-50 rounded p-3 text-sm">{viewingRecord.resolution_notes}</div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Resolve Form */}
              {resolveMode && viewingRecord.status === 'active' && (
                <div className="border-t pt-4 space-y-4">
                  <div className="space-y-2">
                    <Label>Resolution Action *</Label>
                    <Select 
                      value={resolveData.resolution_action} 
                      onValueChange={(val) => setResolveData(prev => ({ ...prev, resolution_action: val }))}
                    >
                      <SelectTrigger data-testid="resolution-action-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="returned_to_stock">Return to Stock</SelectItem>
                        <SelectItem value="scrapped">Scrapped</SelectItem>
                        <SelectItem value="returned_to_supplier">Returned to Supplier</SelectItem>
                        <SelectItem value="warranty_replaced">Warranty Replaced</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      {resolveData.resolution_action === 'returned_to_stock' && 'Item will be released back to available inventory.'}
                      {resolveData.resolution_action === 'scrapped' && 'Item will be removed from inventory.'}
                      {resolveData.resolution_action === 'returned_to_supplier' && 'Item will be removed from inventory.'}
                      {resolveData.resolution_action === 'warranty_replaced' && 'Item will be released. Add replacement via separate restock.'}
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Resolution Notes *</Label>
                    <Textarea
                      value={resolveData.resolution_notes}
                      onChange={(e) => setResolveData(prev => ({ ...prev, resolution_notes: e.target.value }))}
                      placeholder="Required: Describe the resolution..."
                      rows={3}
                      data-testid="resolution-notes-input"
                    />
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            {resolveMode && viewingRecord?.status === 'active' ? (
              <>
                <Button variant="outline" onClick={() => setResolveMode(false)}>
                  Back
                </Button>
                <Button onClick={handleResolve} disabled={resolving} data-testid="resolve-submit-btn">
                  {resolving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Resolve Quarantine
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setViewModalOpen(false)}>
                  Close
                </Button>
                {viewingRecord?.status === 'active' && canResolve && (
                  <Button onClick={() => setResolveMode(true)} data-testid="start-resolve-btn">
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Resolve
                  </Button>
                )}
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MobileFAB onClick={() => { resetCreateForm(); setCreateModalOpen(true); }} data-testid="mobile-fab-quarantine" />
    </Layout>
  );
};

export default Quarantine;
